/*
===============================================================================================================
                                 Copyright 2023-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useContext, useRef, useEffect, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { LocaleContext } from '@/contexts/locale-context';
import { ThemeContext } from '@/contexts/theme-context';
import ErrorReportingHOC from '@/components/error-reporting-hoc';
import TextDiv from '@/components/text-div';
import ClickAway from '@/components/click-away/click-away';
import CalenderIcon from '@/public/icons/calendar.svg';
import { escapeCustomText, unEscapeCustomText } from '@/modules/page-customisation/utilities';
// eslint-disable-next-line import/no-cycle
import EditableTooltip from '@/components/editable-tooltip';
import { getStaticCdnPath } from '@/utilities/static-build';

/**
 * Component that has an inline editable text box, and an edit pen icon button. The box is only editable once
 * you click the icon button, Click the icon again to commit the changes. Click away to cancel.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {func} onChange - Called when the save button is clicked
 * @param {func} onChangeTooltip - Called when tooltip save button is clicked
 * @param {string} content - The text content of the input box
 * @param {string} defaultValueTooltip - The text content of the tooltip input
 * @param {string} fontSize - Css global variable value of the font size for the input
 * @param {string} fontWeight - Css global variable value of the font weight for the input
 * @param {bool}   textDisabled - Whether or not the input text is greyed out
 * @param {boolean} hideValidationErrors - determines whether or not to include spacing for error message
 * @param {boolean} inputMode - determines whether or not to look like a normal text input
 * @param {boolean} dropdownMode - determines whether or not to have styles adjusted for being used as a dropdown
 * @param {boolean} checkboxMode - determines whether or not to look like a checkbox
 * @param {boolean} centered - determines whether or not the input should be centered horizontally
 * @param {string} inputTextAlignment - determines the input text alignment. A value for 'text-align' css property.
 * @param {Object} validationRules - Full description in error-reporting.jsx
 * @param {boolean} showAsRequired - Whether or not should be shown as a required field
 * @param {boolean} readOnly - Whether or not this is a read-only field
 * @param {boolean} errorLeftAligned - Whether or not error message should be left aligned.
 * @param {number} externalWrapperWidth - Optional -  Dynamic width of the container of the inline text
 * @param {boolean} textAreaMode - Whether or not textbox should be rendered using textarea element.
 * @param {string} defaultResetValue - Optional. A text string to display instead of an empty input, when the field gets reset.
 * @param {shape} tooltipConfig - The 'params' object of the tooltip field in config
 * @param {string} minHeight - Optional. The minimum height of the input box. Primarily needed for textareas.
 * @param {string} maxWidth - Optional. The maximum width of the input box.
 * @param {string} subtitle - Optional. A subtitle to display below the input box.
 * @param {boolean} partiallyEditable - Optional. Whether or not the input should be partially editable.
 * @param {string} uneditableContent - Optional. The content that should not be editable.
 * @param {string} textColor - Optional. The color of the text in the input box.
 * @param {string} smallFontStyling - Optional. Whether or not the component should have styling adjustments for smaller fonts.
 */
const InlineEditableText = forwardRef(({
  inputSelectorId,
  onChange,
  deleteOnChange,
  onChangeTooltip,
  defaultValueTooltip,
  maxLength,
  textDisabled,
  fontSize,
  fontWeight,
  hideValidationErrors,
  inputMode,
  checkboxMode,
  asLink,
  centered,
  inputTextAlignment,
  errorLeftAligned,
  defaultResetValue,
  externalWrapperWidth,
  index,
  readOnly,
  calenderIcon,
  tooltipConfig,
  textAreaMode,
  minHeight,
  showAsRequired,
  dropdownMode,
  useContainerWidth,
  subtitle,
  partiallyEditable,
  uneditableContent,
  textColor,
  smallFontStyling,
  shouldTranslateResetValue,
  withTextOverflow,
  ariaLabel,
  error, // Passed in automatically, no need to set
  content, // Passed in automatically, no need to set
  setContent, // Passed in automatically, no need to set
}, ref) => {
  const { textDirection, translate } = useContext(LocaleContext);
  const { primaryColor } = useContext(ThemeContext);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [width, setWidth] = useState(0);
  const [ellipsisTextInput, setEllipsisTextInput] = useState(false);
  const [textAreaHeight, setTextAreaHeight] = useState(null);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  const [chars, setChars] = useState(0);
  const [currentValue, setCurrentValue] = useState(null);
  const inputRef = useRef(null);
  const initialValue = useRef(null);
  const span = useRef();
  const textareaHidden = useRef();
  const inputWrapper = useRef();

  const isMobile = useMediaQuery({ query: '(max-width: 1200px)' });

  const getHiddenSpanOffset = () => {
    if (calenderIcon || dropdownMode) {
      return 100;
    }
    if (textAreaMode && inputMode) {
      return 65;
    }
    return 45;
  };

  useEffect(() => {
    const wrapperWidthToUse = inputMode || dropdownMode ? wrapperWidth : externalWrapperWidth;
    const ellipsis = width > ((wrapperWidthToUse - getHiddenSpanOffset()) / 100) * 95;

    setEllipsisTextInput(ellipsis);
  }, [width, wrapperWidth, externalWrapperWidth, currentValue, isMobile]);

  const syncTextareaHeight = () => {
    const hiddenRef = textareaHidden.current;
    const input = inputRef.current;
    const computedStyle = window.getComputedStyle(span.current);

    if (!hiddenRef || !computedStyle) return;
    // set width and value of a hidden textarea
    hiddenRef.style.width = computedStyle.width === '0px' ? `${wrapperWidth || externalWrapperWidth}px` : computedStyle.width;
    hiddenRef.value = input?.value || defaultResetValue;

    // measure its height
    const innerHeight = hiddenRef.scrollHeight;

    // Measure height of a textarea with a single row
    hiddenRef.value = 'x';
    const singleRowHeight = hiddenRef.scrollHeight;

    // reset the width set earlier
    hiddenRef.style.width = undefined;

    // If no value is given take the height of a single row as a minimum height
    // Otherwise take the height of the content
    const outerHeight = !inputRef.current?.value
      ? singleRowHeight
      : Math.max(innerHeight, singleRowHeight);

    setTextAreaHeight(outerHeight);
  };

  useEffect(() => {
    syncTextareaHeight();
  }, [width, currentValue, ellipsisTextInput]);

  useEffect(() => {
    setWidth(span.current.offsetWidth);
    setChars(currentValue?.length ?? 0);
    if (inputMode || dropdownMode) {
      setWrapperWidth(inputWrapper.current.offsetWidth);
    }
  }, [currentValue, fontsLoaded]);

  useEffect(() => {
    if (editing) {
      inputRef?.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    let valueToUse;
    if (!content?.length && defaultResetValue && !editing) {
      valueToUse = shouldTranslateResetValue ? translate(defaultResetValue) : defaultResetValue;
    } else {
      valueToUse = unEscapeCustomText(content);
    }
    inputRef.current.value = valueToUse;
    setCurrentValue(valueToUse);
  }, [content, translate]);

  useEffect(() => {
    document.fonts?.ready?.then(() => {
      setFontsLoaded(true);
    });
  }, []);

  const keyDownToggleEditing = (event) => {
    if (event.key === 'Enter') {
      setEditing(true);
    }
  };

  const handleBlur = () => {
    const inputElement = inputRef.current;
    inputElement.selectionStart = inputElement.value.length;
    inputElement.selectionEnd = inputElement.value.length;
  };

  const resetInput = () => {
    inputRef.current.value = initialValue.current;
    setCurrentValue(initialValue.current);
  };

  const textSaved = () => {
    handleBlur();
    setEditing(false);
    if (!inputRef?.current.value) {
      resetInput();
    }
  };

  const onTextChange = (val) => {
    setContent(val);
    setCurrentValue(val);
    if (index) {
      onChange(escapeCustomText(val), index);
    }
    if (!index) {
      onChange(escapeCustomText(val));
    }
  };

  const handleClickAway = () => {
    setEditing(false);
    textSaved();
  };

  const keyDownSave = (event) => {
    if (event.key === 'Enter') {
      textSaved();
    }
  };

  const keyDownDelete = (event) => {
    if (event.key === 'Enter') {
      setEditing(false);
      deleteOnChange(index);
    }
  };

  const handleDelete = () => {
    deleteOnChange(index);
  };

  const handleStartEditing = () => {
    initialValue.current = inputRef.current?.value;
    const inputElement = inputRef.current;
    inputElement.selectionStart = inputRef.current?.value.length;
    inputElement.selectionEnd = inputRef.current?.value.length;
  };

  const hasCountElement = editing && maxLength;

  return (
    <div className="container">
      <ClickAway fullWidth shouldWeListen={editing} onClickAway={handleClickAway}>
        <div className={dropdownMode ? 'input-wrapper' : ''} ref={ref}>
          <div className={`wrapper${smallFontStyling ? ' small' : ''}`} ref={inputWrapper}>
            {checkboxMode && (
              <div className="checkbox" />
            )}
            <div className="shadow-wrapper">
              {partiallyEditable && uneditableContent && (
              <div className="prefix">{uneditableContent}</div>
              )}
              <span className="hide" ref={span}>{currentValue}</span>
              {!textAreaMode && (
                <input
                  aria-label={ariaLabel}
                  id={inputSelectorId}
                  ref={inputRef}
                  type="text"
                  dir={textDirection}
                  defaultValue={content}
                  disabled={!editing}
                  maxLength={maxLength}
                  onWheel={(e) => (!ellipsisTextInput ? e.target.blur() : null)}
                  className={`input${textDisabled ? '-disabled' : ''}${useContainerWidth ? ' container-width' : ''}`}
                  onChange={(e) => onTextChange(e.target.value)}
                  onFocus={handleStartEditing}
                  onBlur={handleBlur}
                />
              )}
              {textAreaMode && (
                <>
                  <textarea
                    className="textarea-hide"
                    aria-hidden
                    readOnly
                    ref={textareaHidden}
                    rows={1}
                    tabIndex={-1}
                  />
                  <textarea
                    aria-label={ariaLabel}
                    id={inputSelectorId}
                    ref={inputRef}
                    type="text"
                    dir={textDirection}
                    defaultValue={content}
                    disabled={!editing}
                    maxLength={maxLength}
                    onWheel={(e) => (!ellipsisTextInput ? e.target.blur() : null)}
                    className={`input${textDisabled ? '-disabled' : ''}${useContainerWidth ? ' container-width' : ''}`}
                    onChange={(e) => onTextChange(e.target.value)}
                    onFocus={handleStartEditing}
                    onBlur={handleBlur}
                    rows={1}
                    style={{
                      height: textAreaHeight + 2, // adding 2px to account for the bottom border
                    }}
                  />
                </>
              )}
              <span className={` ${editing ? 'focused' : 'separator'}`}> </span>
              {hasCountElement && (
                <div className="character-count">
                  {`${chars}/${maxLength}`}
                </div>
              )}
            </div>
            {showAsRequired && <span className="required-asterix">*</span>}
            {!readOnly && (
              <>
                {editing ? (
                  <div
                    className="edit"
                    role="button"
                    tabIndex={0}
                    onClick={textSaved}
                    onKeyDown={(e) => keyDownSave(e)}
                    aria-label="Save"
                  >
                    <img src={`${getStaticCdnPath()}/icons/edit.svg`} alt="" className="icon-image" />
                  </div>
                ) : (
                  <div
                    className="edit"
                    role="button"
                    tabIndex={0}
                    onClick={() => setEditing(true)}
                    onKeyDown={(e) => keyDownToggleEditing(e)}
                    aria-label="Edit"
                  >
                    <img src={`${getStaticCdnPath()}/icons/edit.svg`} alt="" className="icon-image" />
                  </div>
                )}
              </>
            )}
            {deleteOnChange && (
            <div
              className="delete"
              role="button"
              tabIndex={0}
              onClick={handleDelete}
              onKeyDown={(e) => keyDownDelete(e)}
              aria-label="Delete"
            >
              <img src={`${getStaticCdnPath()}/icons/trashcan.svg`} alt="" className="icon-image" />
            </div>
            )}
            {calenderIcon && inputMode && (
              <div className={`date-icon-box${textDirection === 'rtl' ? ' rtl-box' : ''}`}>
                <CalenderIcon />
              </div>
            )}
            {tooltipConfig?.enabled && (
              <EditableTooltip
                ariaLabel={`${ariaLabel} ${translate('constants.tooltipAriaLabel')}`}
                inputSelectorId={`${inputSelectorId}-tooltip`}
                onChange={onChangeTooltip}
                defaultTooltip={defaultValueTooltip}
                readOnly={readOnly}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...tooltipConfig}
              />
            )}
          </div>
          {subtitle && (
          <div className="subtitle-wrapper">
            <TextDiv>{translate(subtitle)}</TextDiv>
          </div>
          )}
        </div>
      </ClickAway>
      {!hideValidationErrors && (
      <div
        className={`error${error ? ' visible' : ''}${errorLeftAligned ? ' left' : ''}${textDirection === 'rtl' ? ' rtl' : ''}`}
      >
        {error && <TextDiv selectorId={`${inputSelectorId}-error`}>{error}</TextDiv>}
      </div>
      )}
      <style jsx>
        {`
          .container {
            flex: 1;
            display: flex;
            flex-direction: row;
          }

          .wrapper {
            flex: ${dropdownMode ? '1' : 'unset'};
            align-items: ${checkboxMode ? 'center' : 'unset'};
            column-gap: ${checkboxMode ? '10px' : 'unset'};
            justify-content: ${centered ? 'center' : 'unset'};
            position: relative;
            display: flex;
            border-radius: ${inputMode ? 'var(--border-radius)' : 'unset'};
            border: ${inputMode ? '1px solid var(--color-mid-grey)' : 'unset'};
            width: 100%;
            height: ${(inputMode && !textAreaMode) ? '48px' : 'auto'};
            min-height: ${minHeight?.startsWith('--') ? `var(${minHeight})` : minHeight ?? 'unset'};
          }

          .input-wrapper {
            display: flex;
            flex: 1;
          }

          .shadow-wrapper {
            display: flex;
            flex-direction: column;
            position: relative;
            justify-content: ${textAreaMode && inputMode ? 'unset' : 'center'};
            flex: ${dropdownMode ? '1' : 'unset'};
          }

          .centeredInput {
            justify-content: center;
            margin-left: auto;
            margin-right: auto;
          }

          .subtitle-wrapper {
            margin-top: 8px;
          }

          .checkbox {
            border: 1px solid var(--color-mid-grey);
            border-radius: 3px;
            height: 40px;
            width: 40px;
          }
          .prefix {
            color: var(--color-primary-dark);
            font-size: ${fontSize};
            font-weight: ${inputMode ? 'var(--font-weight-body)' : fontWeight};
            font-family: var(--font-family-body-sans),Arial,Helvetica,sans-serif;
            margin: 0;
            text-align: ${inputTextAlignment || (textDirection === 'rtl' ? 'right' : 'left')};
            text-decoration: ${asLink ? 'underline' : 'unset'};
            line-height: 1.5;
          }

          input, textarea {
            border-bottom: 2px solid var(--color-transparent);
            border-top: none;
            border-left: none;
            border-right: none;
            background-color: var(--color-transparent);
            color: ${textColor ?? 'var(--color-primary-dark)'};
            font-size: ${fontSize};
            font-weight: ${inputMode ? 'var(--font-weight-body)' : fontWeight};
            font-family: var(--font-family-body-sans),Arial,Helvetica,sans-serif;
            height: ${inputMode ? '37px' : '30px'};
            min-width: ${inputMode ? '100px' : 'unset'};
            width: ${withTextOverflow ? 'unset' : `${width + 20}px`};
            padding: ${inputMode ? '10px 0 10px 10px' : '2px 0'};
            margin: 0;
            text-align: ${inputTextAlignment || (textDirection === 'rtl' ? 'right' : 'left')};
            text-decoration: ${asLink ? 'underline' : 'unset'};
            text-overflow: ${withTextOverflow && readOnly ? 'ellipsis' : 'unset'};
          }

          .wrapper.small input, textarea {
            height: auto;
          }

          textarea {
            line-height: 1.5;
            resize: none;
            overflow-y: hidden;
            padding: ${inputMode ? '0 10px 0 0' : '2px 0'};
            margin: ${inputMode ? '10px 0 0 10px' : ''};
          }

          input:focus,
          textarea:focus {
            outline: none;
          }

          textarea.container-width {
            width: calc(100% + 10px);
          }

          .required-asterix {
            font-size: var(--font-size-H1);
            line-height: 1.7;
            transform: translateX(${textDirection === 'rtl' ? 7 : -7}px);
          }

          img {
            margin-left: ${textAreaMode ? 10 : 0}px;
          }

          img:hover {
            opacity: 0.8;
          }

          .character-count {
            background-color: var(--color-white);
            font-size: var(--font-size-error);
            text-align: right;
            position: absolute;
            bottom: -16px;
            right: 0;
            z-index: 1;
            color: var(--color-primary-dark);
          }

          .hide {
            font-family: var(--font-family-body-sans),Arial,Helvetica,sans-serif;
            display: inline-block;
            height: 1px;
            font-size: ${fontSize};
            font-weight: ${inputMode ? 'var(--font-weight-body)' : fontWeight};
            opacity: 0;
            width: fit-content;
            pointer-events: none;
            word-break: break-word;
            max-width: ${(externalWrapperWidth - getHiddenSpanOffset() > 0) ? externalWrapperWidth - getHiddenSpanOffset() : '700'}px;
          }

          .textarea-hide {
            pointer-events: none;
            position: absolute;
            line-height: 1.5;
            visibility: hidden;
            padding: 0;
            font-size: ${fontSize};
            width: ${width}px;
          }

          .input-disabled {
            color: var(--color-mid-grey);
          }

          .input,
          .input-disabled {
            border-bottom: ${error ? '2px solid var(--color-error-red)' : '2px solid var(--color-transparent)'};
          }

          .edit {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transform: scale(0.9);
            padding: ${textDirection === 'rtl' ? '0 0 10px' : '0 10px 0 0'};
          }

          .wrapper.small .edit {
            transform: scale(0.6) translateX(-10px);
          }

          .delete {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            margin-left: 5px;
          }
          
          .error {
            visibility: hidden;
          }

          .error.visible {
            font-size: var(--font-size-error);
            font-weight: var(--font-weight-footer);
            color: var(--color-error-red);
            visibility: visible;
            min-width: 60px;
            z-index: 1; 
          }

          .error.left,
          .error.rtl {
            text-align: left;
          }

          .error.left.rtl {
            text-align: right;
          }

          .focused {
            height: 2px;
            width: 100%;
            background: ${primaryColor};
            display: block;
            transition: all .15s linear;
            transform: translateX(0) translateY(-2px);
            transform-origin: 50%;
            opacity: ${error ? 0 : 1};
          }

          .date-icon-box {
            border-radius: 3px;
            border: solid var(--color-mid-grey);
            border-width: 0 0 0 1px;
            cursor: pointer;
            height: 48px;
            padding: 10px;
            position: absolute;
            right: -1px;
            top: -1px;
            width: 48px;
          }

          .date-icon-box.rtl-box {
            border-width: 0 1px 0 0;
            left: -1px;
            top: -1px;
          }


          .separator {
            height: 2px;
            width: 100%;
            background: ${primaryColor};
            display: block;
            opacity: 0;
            transform: translateX(0) translateY(-2px);
            transition: all .15s linear;
            transform-origin: 50%;
        }

          @media only screen and (max-width: 1200px) {
            .wrapper {
              max-width: 100%;
            }
          }

          @media only screen and (max-width: 768px) {

            .textarea-hide {
              overflow: scroll;
            }
          }

        `}
      </style>
    </div>
  );
});

InlineEditableText.defaultProps = {
  inputSelectorId: null,
  onChange: () => {},
  deleteOnChange: null,
  onChangeTooltip: () => {},
  maxLength: null,
  textDisabled: false,
  fontSize: 'var(--font-size-body)',
  fontWeight: 'var(--font-weight-H1)',
  hideValidationErrors: false,
  inputMode: false,
  dropdownMode: false,
  externalWrapperWidth: null,
  errorLeftAligned: false,
  index: null,
  textAreaMode: false,
  checkboxMode: false,
  asLink: false,
  readOnly: false,
  calenderIcon: false,
  showAsRequired: false,
  centered: false,
  inputTextAlignment: null,
  useContainerWidth: false,
  maxWidth: 100,
  error: null,
  minHeight: null,
  subtitle: null,
  partiallyEditable: false,
  smallFontStyling: false,
  shouldTranslateResetValue: true,
  withTextOverflow: false,
  uneditableContent: null,
  textColor: null,
  validationRules: {
    onSubmit: null,
    required: '',
    submitErrors: null,
    tooLongError: '',
  },
  content: null,
  defaultValueTooltip: null,
  defaultResetValue: null,
  tooltipConfig: {},
  ariaLabel: null,
};

InlineEditableText.propTypes = {
  inputSelectorId: PropTypes.string,
  onChange: PropTypes.func,
  deleteOnChange: PropTypes.func,
  onChangeTooltip: PropTypes.func,
  content: PropTypes.string,
  setContent: PropTypes.func.isRequired,
  defaultValueTooltip: PropTypes.string,
  maxLength: PropTypes.number,
  textDisabled: PropTypes.bool,
  fontSize: PropTypes.string,
  fontWeight: PropTypes.string,
  hideValidationErrors: PropTypes.bool,
  inputMode: PropTypes.bool,
  dropdownMode: PropTypes.bool,
  externalWrapperWidth: PropTypes.number,
  errorLeftAligned: PropTypes.bool,
  index: PropTypes.number,
  textAreaMode: PropTypes.bool,
  checkboxMode: PropTypes.bool,
  asLink: PropTypes.bool,
  readOnly: PropTypes.bool,
  calenderIcon: PropTypes.bool,
  centered: PropTypes.bool,
  showAsRequired: PropTypes.bool,
  useContainerWidth: PropTypes.bool,
  inputTextAlignment: PropTypes.string,
  error: PropTypes.string,
  defaultResetValue: PropTypes.string,
  minHeight: PropTypes.string,
  maxWidth: PropTypes.number,
  subtitle: PropTypes.string,
  partiallyEditable: PropTypes.bool,
  smallFontStyling: PropTypes.bool,
  shouldTranslateResetValue: PropTypes.bool,
  withTextOverflow: PropTypes.bool,
  uneditableContent: PropTypes.string,
  textColor: PropTypes.string,
  validationRules: PropTypes.shape({
    onSubmit: PropTypes.arrayOf(PropTypes.string),
    requiredError: PropTypes.string,
    submitErrors: PropTypes.arrayOf(PropTypes.string),
    tooLongError: PropTypes.string,
  }),
  tooltipConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    defaultResetValue: PropTypes.string,
    onSubmitValidationRules: PropTypes.arrayOf(PropTypes.string),
    onChangeValidationRules: PropTypes.arrayOf(PropTypes.string),
    maxLength: PropTypes.number,
    tooLongError: PropTypes.string,
  }),
  ariaLabel: PropTypes.string,
};

InlineEditableText.displayName = 'InlineEditableText';

export default ErrorReportingHOC(InlineEditableText);
